/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Calendar,
  Clock,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Twitter,
  Image as ImageIcon,
  Play,
  Layers,
  Save,
  Send,
  TrendingUp,
} from 'lucide-react';
import VideoPlayer from '../../../shared/components/VideoPlayer';
import moment from 'moment';
import ContentMarketingService from '../../services/contentMarketing.services';
import { capitalize } from 'lodash';
import { MarketingPlatform } from '../../../../data/types/contentMarketing.interface';

interface SchedulePostPanelProps {
  post: any;
  onClose: () => void;
  onSchedulePost: (postId: number, scheduleData: any, action: 'update' | 'schedule') => void;
  onSavePost: (postId: number, updatedData: any, action: 'update' | 'publish') => void;
  isLoading: boolean;
}

export default function SchedulePostPanel({ post, onClose, onSchedulePost, onSavePost, isLoading }: SchedulePostPanelProps) {
  const [postTitle, setPostTitle] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [tags, setTags] = useState('');
  const [caption, setCaption] = useState('');
  const [activePlatform, setActivePlatform] = useState('YouTube');
  const [marketingDetails, setMarketingDetails] = useState<MarketingPlatform[]>([]);
  const [publishedStatus, setPublishedStatus] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [privacyStatus, setPrivacyStatus] = useState<'public' | 'private'>('private');
  const [actionButtonVal, setActionButtonVal] = useState<'publish' | 'schedule' | 'update'>('publish');

  // Initial data for dirty check
  const [initialData, setInitialData] = useState({
    title: '',
    hashtags: '',
    tags: '',
    caption: ''
  });

  const isDirty = useMemo(() => {
    return (
      postTitle !== initialData.title ||
      hashtags !== initialData.hashtags ||
      tags !== initialData.tags ||
      caption !== initialData.caption
    );
  }, [postTitle, hashtags, tags, caption, initialData]);

  const isAllowedToPublish = () => (isDirty && publishedStatus === 'published') || publishedStatus !== 'published';

  const getContentDetails = async (id: number) => {
    try {
      const content = await ContentMarketingService.getContentDetails(post?.contentType || 'video', id, activePlatform);
      setMarketingDetails(content.marketingPlatformDetails);
    } catch (err) {
      console.log(err);
    }
  };

  const initializeData = () => {
    if (post) {
      setPostTitle(post.title || '');
      setTags(post.tags || '');
      setHashtags(post.keywords || '');
      setCaption(post.text || '');
      setMediaPreviewUrl(post.url || '');
      setSelectedPlatforms(post.platforms || []);
      setPrivacyStatus(post.privacyStatus || 'private');

      setInitialData({
        title: post.title || '',
        hashtags: post.keywords || '',
        tags: post.tags || '',
        caption: post.text || ''
      });

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduleDate(tomorrow.toISOString().split('T')[0]);
      setScheduleTime('09:00');
    }
  };

  // Load initial data
  useEffect(() => {
    if (post) {
      initializeData();
      getContentDetails(post.contentId);
    }
  }, [post]);

  useEffect(() => {
    const marketingData = marketingDetails.find((data) => data.platform === activePlatform);
    if (!marketingData) {
      setPostTitle(initialData.title || '');
      setTags(initialData.tags || '');
      setHashtags(initialData.hashtags || '');
      setCaption(initialData.caption || '');
      setPublishedStatus('');
      setActionButtonVal('publish');
      setSelectedPlatforms(['YouTube']);
      return;
    }

    // prevent unnecessary re-renders
    if (
      marketingData.title === postTitle &&
      marketingData.keywords === hashtags &&
      marketingData.text === caption &&
      marketingData.tags === tags
    ) return;

    setInitialData({
      title: marketingData.title || '',
      hashtags: marketingData.keywords || '',
      tags: marketingData.tags || '',
      caption: marketingData.text || ''
    });
    setPostTitle(marketingData.title || '');
    setHashtags(marketingData.keywords || '');
    setTags(marketingData.tags || '');
    setCaption(marketingData.text || '');
    setSelectedPlatforms(marketingData.platform ? [marketingData.platform] : []);
    setPrivacyStatus(marketingData.privacyStatus || 'private');
    setPublishedStatus(marketingData.publishedStatus || '');
    if (marketingData?.scheduledDate) {
      setActionButtonVal('schedule')
      setScheduleDate(new Date(marketingData?.scheduledDate).toISOString().split('T')[0]);
      setScheduleTime(moment(marketingData?.scheduledDate).local().format('HH:mm'));
    }
  }, [activePlatform, marketingDetails]);

  if (!post) return null;

  const createUtcDate = (date: string, time: string) => {
    if (!date || !time) return null;
    const m = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm", true);
    if (!m.isValid()) return null;
    return m.utc().toISOString();
  };

  const handleSchedulePost = () => {
    const scheduleData = {
      title: postTitle,
      keywords: hashtags,
      tags,
      text: caption,
      platforms: selectedPlatforms,
      scheduledDate: createUtcDate(scheduleDate, scheduleTime),
      contentType: post.contentType,
      privacyStatus
    };
    onSchedulePost(post.contentId, scheduleData, (isDirty && publishedStatus === 'published') ? 'update' : 'schedule');
    onClose();
  };

  const handleSavePost = () => {
    const updatedData = {
      title: postTitle,
      keywords: hashtags,
      tags,
      text: caption,
      platforms: selectedPlatforms,
      contentType: post.contentType,
      privacyStatus,
      publishingLink: marketingDetails.find((data) => data.platform === activePlatform)?.publishingLink || ''
    };
    onSavePost(post.contentId, updatedData, (isDirty && publishedStatus === 'published') ? 'update' : 'publish');
  };

  const renderMediaPreview = () => {
    if (!post.url && !mediaPreviewUrl) {
      return (
        <div className="h-[200px] w-full rounded-lg border border-dashed border-[#D1D5DB] bg-[#F9FAFB] flex flex-col items-center justify-center">
          <ImageIcon size={32} className="text-[#9CA3AF] mb-2" />
          <span className="text-sm text-[#9CA3AF]">No media</span>
        </div>
      );
    }

    const isVideo = post.format === 'short' || post.format === 'long';
    const isCarousel = post.format === 'carousel';

    return (
      <div className="relative h-[200px] w-full rounded-lg overflow-hidden border border-[#E5E7EB]">
        <VideoPlayer videoUrl={mediaPreviewUrl || ''} thumbnail={post?.thumbnail} />

        {isCarousel && post.slideCount && (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded backdrop-blur-sm">
            {post.slideCount} slides
          </div>
        )}

        {isVideo && post.duration && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded backdrop-blur-sm">
            {post.duration}
          </div>
        )}

        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-white/85 rounded-full flex items-center justify-center shadow-lg backdrop-blur-sm">
              <Play size={18} className="text-[#111827] ml-0.5" fill="currentColor" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const getPlatformConfig = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return { icon: Instagram, label: 'Instagram', color: 'text-[#E4405F]' };
      case 'Facebook':
        return { icon: Facebook, label: 'Facebook', color: 'text-[#1877F2]' };
      case 'YouTube':
        return { icon: Youtube, label: 'YouTube', color: 'text-[#FF0000]' };
      case 'LinkedIn':
        return { icon: Linkedin, label: 'LinkedIn', color: 'text-[#0A66C2]' };
      case 'Twitter':
        return { icon: Twitter, label: 'Twitter', color: 'text-[#1DA1F2]' };
      default:
        return null;
    }
  };

  const platforms = ['YouTube', 'Instagram', 'Facebook', 'LinkedIn', 'Twitter'];

  // Calculate SEO Score based on hashtags
  const calculateSEOScore = () => {
    const hashtagCount = (hashtags.match(/#[\w]+/g) || []).length;
    if (hashtagCount === 0) return 0;
    if (hashtagCount >= 1 && hashtagCount <= 4) return Math.min(hashtagCount * 20, 80);
    if (hashtagCount >= 5 && hashtagCount <= 10) return 100;
    return Math.max(100 - (hashtagCount - 10) * 5, 60);
  };

  const seoScore = calculateSEOScore();
  const getSEOColors = () => {
    if (seoScore >= 80) return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' };
    if (seoScore >= 50) return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' };
    return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' };
  };
  const seoColors = getSEOColors();

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 opacity-100" onClick={onClose} />

      {/* Slide-in Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-[600px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out translate-x-0">
        {/* Panel Header */}
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-[#111827]">Post</h2>
            <p className="text-sm text-[#6B7280] mt-0.5">Edit and schedule your post content</p>
          </div>
          <button onClick={onClose} className="p-2 text-[#6B7280] hover:bg-[#F9FAFB] rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Platform Tabs */}
        {platforms.length > 0 && (
          <div className="sticky top-[72px] bg-white border-b border-[#E5E7EB] px-6 z-10">
            <div className="flex items-center gap-1 overflow-x-auto py-2">
              {platforms.map((platform) => {
                const config = getPlatformConfig(platform);
                if (!config) return null;

                const Icon = config.icon;
                const isActive = activePlatform === platform;

                return (
                  <button
                    key={platform}
                    onClick={() => {
                      setActivePlatform(platform);
                      setSelectedPlatforms([platform]);
                    }}
                    className={`px-4 py-2.5 text-sm font-medium transition-all relative flex items-center gap-2 whitespace-nowrap
              ${isActive
                        ? 'text-[#F67570]'
                        : 'text-[#6B7280] hover:text-[#111827]'
                      }
            `}
                  >
                    <Icon size={16} className={config.color} />
                    {config.label}

                    {/* Active underline */}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F67570]" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Panel Content - Scrollable */}
        <div className="h-[calc(100%-140px)] overflow-y-auto px-6 py-5 pb-24">
          {publishedStatus && <div className="flex items-center justify-end">
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full border ${seoColors.border} ${seoColors.bg}`}
            >
              <span className={`text-xs font-semibold ${seoColors.text}`}>{capitalize(publishedStatus)}</span>
            </div>
          </div>}

          {/* Post Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#111827] mb-2">Post Title *</label>
            <input
              type="text"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              placeholder="Enter post title..."
              className="w-full h-10 px-3 break-all text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-1 focus:ring-[#F67570]"
            />
          </div>

          {/* Hashtags & SEO Score */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#111827]">Hashtags</label>
              <div
                className={`flex items-center gap-2 px-3 py-1 rounded-full border ${seoColors.border} ${seoColors.bg}`}
              >
                <TrendingUp size={14} className={seoColors.text} />
                <span className={`text-xs font-semibold ${seoColors.text}`}>SEO Score: {seoScore}%</span>
              </div>
            </div>
            <textarea
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              placeholder="Add hashtags (e.g., #marketing #socialmedia #content)"
              rows={3}
              className="w-full px-3 py-2 break-all text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-1 focus:ring-[#F67570] resize-none"
            />
            <p className="text-xs text-[#6B7280] mt-1.5">
              {(() => {
                const count = (hashtags.match(/#[\w]+/g) || []).length;
                if (count === 0) return 'Add hashtags to improve SEO score (Recommended: 5-10)';
                if (count >= 1 && count <= 4)
                  return `${count} hashtag${count > 1 ? 's' : ''} added. Add ${5 - count} more for optimal SEO.`;
                if (count >= 5 && count <= 10) return `${count} hashtags - Optimal for SEO! 🎯`;
                return `${count} hashtags - Consider reducing to 8-10 for best results.`;
              })()}
            </p>
          </div>

          {/* Tags */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#111827]">Tags</label>
            </div>
            <textarea
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Add tags (e.g., marketing)"
              rows={3}
              className="w-full px-3 py-2 break-all text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-1 focus:ring-[#F67570] resize-none"
            />
          </div>

          {/* Media Preview Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#374151] mb-3">Media Preview</label>
            {renderMediaPreview()}
            {<div className="mt-2 flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB]">
                {post.format === 'carousel' && <Layers size={12} className="mr-1" />}
                {(post.format === 'short' || post.format === 'long') && <Play size={12} className="mr-1" />}
                {(post.format || post.videoType || '').toUpperCase() || 'short'}
              </span>
            </div>}
          </div>

          {/* Caption Editor */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#374151] mb-2">Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write your caption here..."
              rows={8}
              className="w-full px-4 py-3 break-all text-sm bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#F67570]/20 resize-none"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-[#9CA3AF]">{caption.length} characters</span>
            </div>
          </div>

          {/* Privacy Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#111827] mb-2">
              Privacy Type
            </label>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="public"
                  checked={privacyStatus === 'public'}
                  onChange={() => setPrivacyStatus('public')}
                  className="accent-[#F67570]"
                />
                <span className="text-sm text-[#374151] ml-2">Public</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="private"
                  checked={privacyStatus === 'private'}
                  onChange={() => setPrivacyStatus('private')}
                  className="accent-[#F67570]"
                />
                <span className="text-sm text-[#374151] ml-2">Private</span>
              </label>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-[#111827] mb-2">
              Post/Schedule
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="publish"
                  checked={actionButtonVal === 'publish'}
                  onChange={() => setActionButtonVal('publish')}
                  className="accent-[#F67570]"
                />
                <span className="text-sm text-[#374151] ml-2">Publish Now</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="schedule"
                  checked={actionButtonVal === 'schedule'}
                  onChange={() => setActionButtonVal('schedule')}
                  className="accent-[#F67570]"
                />
                <span className="text-sm text-[#374151] ml-2">Schedule</span>
              </label>
            </div>
          </div>

          {/* Schedule Date & Time */}
          {actionButtonVal === 'schedule' && <div className="mb-6">
            <label className="block text-sm font-medium text-[#374151] mb-3">Schedule Date & Time</label>
            <div className="grid grid-cols-2 gap-3">
              {/* Date Picker */}
              <div>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#F67570]/20"
                  />
                </div>
              </div>

              {/* Time Picker */}
              <div>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#F67570]/20"
                  />
                </div>
              </div>
            </div>
          </div>}

          {/* Action Buttons */}
          <div className="bottom-0 left-0 right-0 py-2">
            <div className="flex items-center gap-3">
              <button
                onClick={handleSavePost}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] rounded-lg transition-colors"
                disabled={isLoading || actionButtonVal === 'schedule' || selectedPlatforms.length === 0 || (!isAllowedToPublish())}
              >
                <Save size={16} />
                {(isDirty && publishedStatus === 'published') ? 'Update' : 'Publish Now'}
              </button>
              <button
                onClick={handleSchedulePost}
                disabled={selectedPlatforms.length === 0 || !scheduleDate || !scheduleTime || isLoading || actionButtonVal === 'publish'}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#F67570] hover:bg-[#F45C56] rounded-lg transition-colors disabled:bg-[#D1D5DB] disabled:cursor-not-allowed"
              >
                <Send size={16} />
                {(isDirty && publishedStatus === 'scheduled') ? 'Update' : 'Schedule Post'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
